import { type JSX, useEffect, useState } from 'react';
import { generatePath, useLocation, useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING } from 'Config/PathTemplates';
import { TPRM_SUCCESS_MESSAGE_SERVICE } from 'Config/Text';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { ServiceForNonTprmUser, ThirdPartyForNonTprmUser, ThirdPartyResponse, ThirdPartyService } from 'Models/TPRM';

import { SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_ID, SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_NAME } from '../ServiceDashboard/ServiceDashboard';
import { ManageThirdPartyServiceFormFields } from './Components/ManageThirdPartyServiceFormFields/ManageThirdPartyServiceFormFields';

interface UrlPathParams {
    thirdPartyId: string;
    serviceId: string;
}

export interface ManageThirdPartyServiceProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    authHandler: AuthHandler;
    tprmApi: ClientTPRMApi;
}

/** Allows the user to update or delete an existing third-party service. Note that third-party services are created via the `TprmIntake` component, not via this component. */
export const ManageThirdPartyService = (props: ManageThirdPartyServiceProps): JSX.Element => {
    const { thirdPartyId, serviceId } = useParams<keyof UrlPathParams>() as UrlPathParams;

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    /** Present in the URL if and only if the user navigated here from the Service Dashboard page. */
    const serviceDashboardOriginServiceId = searchParams.get(SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_ID);
    /** Present in the URL if and only if the user navigated here from the Service Dashboard page. */
    const serviceDashboardOriginServiceName = searchParams.get(SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_NAME);

    const userRoles = props.authHandler.getUserRoles();
    const userHasFullTprmAccess = userHasAuthorizedRole(userRoles, TPRM_ROLES);

    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);
    const [service, setService] = useState<ThirdPartyService | ServiceForNonTprmUser>();
    const [thirdParty, setThirdParty] = useState<ThirdPartyResponse | ThirdPartyForNonTprmUser>();
    const [toastContent, setToastContent] = useState<{ variant: 'success' | 'failure'; text: string }>();

    useEffect(() => {
        const getThirdParty = async (): Promise<void> => {
            try {
                setThirdParty((userHasFullTprmAccess ? await props.tprmApi.getThirdPartyDetails(thirdPartyId) : await props.tprmApi.getThirdPartyDetailsForNonTprmUser(thirdPartyId)).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        const getThirdPartyService = async (): Promise<void> => {
            try {
                setService((userHasFullTprmAccess ? await props.tprmApi.getServiceDetails(thirdPartyId, serviceId) : await props.tprmApi.getServiceDetailsForNonTprmUser(thirdPartyId, serviceId)).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParty();
        getThirdPartyService();
    }, [serviceId, thirdPartyId, props.tprmApi, userHasFullTprmAccess]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (service === undefined || thirdParty === undefined) {
        return <Placeholder />;
    }

    const headerTitle = 'Manage Third-Party Service';

    return (
        <>
            {toastContent && <TextToast clearToast={() => setToastContent(undefined)} {...toastContent} />}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(TPRM_THIRD_PARTIES_AND_SERVICES_LISTING)}>Third Parties and Services</BreadcrumbLink>
                        {serviceDashboardOriginServiceId && serviceDashboardOriginServiceName && <BreadcrumbLink link={generatePath(TPRM_SERVICE_DASHBOARD, { third_party_id: thirdPartyId, service_id: serviceDashboardOriginServiceId })}>{`${thirdParty.name} - ${serviceDashboardOriginServiceName}`}</BreadcrumbLink>}
                        <BreadcrumbText>{headerTitle}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={headerTitle}
                body={[
                    {
                        content: <ManageThirdPartyServiceFormFields artificialIntelligenceApi={props.artificialIntelligenceApi} userHasFullTprmAccess={userHasFullTprmAccess} thirdParty={thirdParty} service={service} tprmApi={props.tprmApi} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmitFailed={(text) => setToastContent({ variant: 'failure', text })} onFormSubmittedSuccessfully={async () => setToastContent({ variant: 'success', text: TPRM_SUCCESS_MESSAGE_SERVICE })} />,
                    },
                ]}
            />
        </>
    );
};
